"use client";

import { DisabledCabinet } from "@/components/arcade/disabled-cabinet";
import { isLobbyHibernating } from "@/components/lobby/disabled-ticker-source";
import { TVOverlay } from "@/components/tv-overlay";
import { ARCADE_STYLES } from "@/constants/arcade-styles";
import { isFlagOn } from "@henlo/feature-flags";
import dynamic from "next/dynamic";
import { MutableRefObject, memo, useMemo } from "react";
import { EnhancedLiveTicker } from "./enhanced-live-ticker";
import type { TetrisAudioState } from "./music-player";

// Sourced from the hibernation manifest — flip via FLAG_TETRIS_READS_DISABLED
// env override without a code change. Same flag the disabled-ticker-source
// already consults, keeping the kill-switch in one place.
const TETRIS_DISABLED = isFlagOn("TETRIS_READS_DISABLED");
const MARQUEE_ROW_BULBS = Array.from({ length: 13 });
const MARQUEE_COLUMN_BULBS = Array.from({ length: 7 });
const HIBERNATING_LIVE_LEFT_STYLE = {
  background: "rgba(10, 10, 10, 0.95)",
  borderRight: `1px solid rgba(255, 255, 255, 0.06)`,
  boxShadow: `4px 0 12px rgba(0, 0, 0, 0.8)`,
  minWidth: "70px",
};
const HIBERNATING_LIVE_RIGHT_STYLE = {
  background: "rgba(10, 10, 10, 0.95)",
  borderLeft: `1px solid rgba(255, 255, 255, 0.06)`,
  boxShadow: `-4px 0 12px rgba(0, 0, 0, 0.8)`,
  minWidth: "70px",
};

// Lazy load TetrisGame for better code splitting
const TetrisGame = dynamic(
  () =>
    import("@/components/tetris/tetris-game").then((mod) => ({
      default: mod.TetrisGame,
    })),
  {
    ssr: false,
  },
);

interface TVScreenProps {
  onAudioStateChange: (state: TetrisAudioState) => void;
  audioControlRef: MutableRefObject<{
    play: () => void;
    pause: () => void;
  } | null>;
}

// Memoized TV Screen component
export const TVScreen = memo(({ onAudioStateChange, audioControlRef }: TVScreenProps) => {
  const hibernating = isLobbyHibernating();

  // Lights-off treatment for hibernation:
  // - The "LIVE" rim becomes an extinguished bulb. Filament cooling, not pulsing.
  // - Border desaturates to a near-black grey rim (no red).
  // - Label becomes a small all-caps "AFTER HOURS" in dim moondust.
  const liveLeft = useMemo(
    () => (hibernating ? HIBERNATING_LIVE_LEFT_STYLE : ARCADE_STYLES.LIVE_INDICATOR.left),
    [hibernating],
  );

  const liveRight = useMemo(
    () => (hibernating ? HIBERNATING_LIVE_RIGHT_STYLE : ARCADE_STYLES.LIVE_INDICATOR.right),
    [hibernating],
  );

  return (
    <div className="space-y-2">
      {/* Event Banner / Live Marquee */}
      <div className="relative overflow-hidden rounded-md p-2" style={ARCADE_STYLES.TV_FRAME}>
        <div
          className="relative flex h-10 items-center overflow-hidden rounded-md"
          style={ARCADE_STYLES.TICKER_CONTAINER}
        >
          {/* TV Overlay effect */}
          <TVOverlay intensity="light" />

          {/* State indicator: red blinking LIVE when active; extinguished bulb + flickering AFTER HOURS during hibernation */}
          <div className="absolute left-0 z-30 flex h-full items-center gap-1.5 px-4" style={liveLeft}>
            {hibernating ? (
              <>
                <div
                  className="kansei-flicker-a size-2 rounded-full"
                  style={{
                    background: "radial-gradient(circle, #2a2a32 0%, #0a0a0b 80%)",
                    border: "1px solid rgba(255,255,255,0.05)",
                    boxShadow: "inset 0 1px 1px rgba(0,0,0,0.85)",
                  }}
                />
                <span
                  className="kansei-flicker-b font-pressStart2P text-[7px] uppercase tracking-[0.22em]"
                  style={{ color: "#71717A", letterSpacing: "0.22em" }}
                >
                  AFTER HOURS
                </span>
              </>
            ) : (
              <>
                <div
                  className="bg-red-500 shadow-red-500/50 size-2 animate-pulse rounded-full shadow-lg"
                />
                <span
                  className="font-pressStart2P text-[8px] uppercase text-white drop-shadow-[0_2px_4px_rgba(255,0,0,0.8)]"
                >
                  LIVE
                </span>
              </>
            )}
          </div>

          {/* Right side border for balance — tone-matched */}
          <div className="absolute right-0 z-30 h-full" style={liveRight} />


          {/* Enhanced Live Ticker */}
          <EnhancedLiveTicker speed={35} />
        </div>
      </div>

      {/* TV Screen with Tetris Game — proscenium-framed cinema. Marquee
          bulbs run all four edges; side drapes wash cobalt over the screen
          interior. Hibernation: bulbs all extinguished, drapes barely lit.
          Single-amber-stamp discipline holds — no amber in the bezel chrome;
          amber lives only inside the screen (Tetris O-piece in the moonscape
          autoplayer when active, or the HENLO globe in the cabinet sign). */}
      <div className="relative rounded-md p-3" style={ARCADE_STYLES.TV_FRAME}>
        <MarqueeBulbRow position="top" lit={!hibernating} />
        <MarqueeBulbColumn position="left" lit={!hibernating} />
        <MarqueeBulbColumn position="right" lit={!hibernating} />
        <MarqueeBulbRow position="bottom" lit={!hibernating} />

        <div
          className="relative flex flex-col overflow-hidden rounded-md"
          style={ARCADE_STYLES.TV_SCREEN}
        >
          {/* Proscenium drapes — soft cobalt wash from top + sides of the
              screen. Frames the cabinet like a movie-theater stage without
              literal curtains. Hibernation: thinner, dimmer; reads as the
              room remembering it was once lit. */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 z-[2] h-12"
            style={{
              background:
                "linear-gradient(180deg, rgba(62, 97, 250, 0.16) 0%, rgba(62, 97, 250, 0.04) 50%, transparent 100%)",
              mixBlendMode: "screen",
            }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 left-0 z-[2] w-16"
            style={{
              background:
                "linear-gradient(90deg, rgba(62, 97, 250, 0.16) 0%, rgba(62, 97, 250, 0.04) 50%, transparent 100%)",
              mixBlendMode: "screen",
            }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-[2] w-16"
            style={{
              background:
                "linear-gradient(270deg, rgba(62, 97, 250, 0.16) 0%, rgba(62, 97, 250, 0.04) 50%, transparent 100%)",
              mixBlendMode: "screen",
            }}
          />

          {/* Tetris Game Container */}
          <div
            className="relative h-[518px] w-full overflow-hidden rounded-lg"
            style={ARCADE_STYLES.TETRIS_CONTAINER}
          >
            <TetrisGame
              onAudioStateChange={onAudioStateChange}
              audioControlRef={audioControlRef}
              disabled={TETRIS_DISABLED}
            />
            {TETRIS_DISABLED && <DisabledCabinet />}
          </div>
        </div>
      </div>

    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────
// Marquee bulb primitives — extruded glass with a cooled filament hint
// during hibernation. When the room flips live, every bulb across the
// proscenium glows amber. Single-amber-stamp discipline is preserved
// (the bulbs are GROUP-lit; the rest of the bezel stays moondust).
// ─────────────────────────────────────────────────────────────────────────

function Bulb({ lit }: { lit: boolean }) {
  return (
    <span
      className="block size-[7px] rounded-full"
      style={{
        background: lit
          ? "radial-gradient(circle at 30% 25%, #fff8d8 0%, #fbbf24 50%, #92400e 100%)"
          : "radial-gradient(circle at 30% 25%, #1c1c24 0%, #0a0a0d 75%, #050507 100%)",
        border: "1px solid rgba(62, 97, 250, 0.25)",
        boxShadow: lit
          ? "0 0 8px rgba(251, 191, 36, 0.7), 0 0 14px rgba(251, 191, 36, 0.35), inset 0 1px 1px rgba(255,255,255,0.5)"
          : "inset 0 1px 1px rgba(0,0,0,0.7), inset 0 -1px 1px rgba(255,255,255,0.04)",
      }}
    />
  );
}

function MarqueeBulbRow({
  position,
  lit,
  count = 13,
}: {
  position: "top" | "bottom";
  lit: boolean;
  count?: number;
}) {
  const offset = position === "top" ? "-top-[4px]" : "-bottom-[4px]";
  return (
    <div
      className={`pointer-events-none absolute inset-x-3 ${offset} z-20 flex items-center justify-between`}
    >
      {(count === 13 ? MARQUEE_ROW_BULBS : Array.from({ length: count })).map((_, i) => (
        <Bulb key={i} lit={lit} />
      ))}
    </div>
  );
}

function MarqueeBulbColumn({
  position,
  lit,
  count = 7,
}: {
  position: "left" | "right";
  lit: boolean;
  count?: number;
}) {
  const offset = position === "left" ? "-left-[4px]" : "-right-[4px]";
  return (
    <div
      className={`pointer-events-none absolute inset-y-3 ${offset} z-20 flex flex-col items-center justify-between`}
    >
      {(count === 7 ? MARQUEE_COLUMN_BULBS : Array.from({ length: count })).map((_, i) => (
        <Bulb key={i} lit={lit} />
      ))}
    </div>
  );
}

TVScreen.displayName = "TVScreen";
